import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Building } from '../../../types/protocols';
import { BaseComponentImpl } from '../../../types/implementations';
import BlueprintWithMarkers from '../../../Components/BlueprintWithMarkers';
import LevelNavigator from '../../../Components/LevelNavigator';
import {
    DrawingView,
    asSatelliteMarkers,
    defaultDrawingView,
    drawingViews,
    isPlacedOn,
} from '../../../Helpers/componentDrawing';
import './ComponentDrawingPicker.css';

interface ComponentDrawingPickerProps {
    building: Building;
    /** Every live component of the building; each is drawn wherever it carries a pin. */
    components: BaseComponentImpl[];
    /** Keys the user may pick (the caller's filter applied). Other components are drawn muted and unclickable. */
    pickableKeys: ReadonlySet<string>;
    multiSelect: boolean;
    selectedKey: string | null;
    selectedKeys: ReadonlySet<string>;
    /** A marker was chosen: select it (single) or toggle it (multi). */
    onPick: (componentKey: string) => void;
}

const viewId = (view: Pick<DrawingView, 'kind' | 'levelKey'>): string =>
    view.kind === 'level' ? `level:${view.levelKey}` : view.kind;

/** The size of an element, kept current through ResizeObserver (zero until measured, or where the observer is missing). */
const useElementSize = <T extends HTMLElement>(): [React.RefObject<T | null>, { width: number; height: number }] => {
    const ref = useRef<T>(null);
    const [size, setSize] = useState({ width: 0, height: 0 });
    useLayoutEffect(() => {
        const element = ref.current;
        if (!element || typeof ResizeObserver === 'undefined') return;
        const update = () => setSize({ width: element.clientWidth, height: element.clientHeight });
        const observer = new ResizeObserver(update);
        observer.observe(element);
        update();
        return () => observer.disconnect();
    }, []);
    return [ref, size];
};

/**
 * The "Ritning/Karta" tab of the component picker: one pill row to switch
 * between the satellite image, every level and the takplan, and the chosen
 * picture with its component markers, scaled so the whole picture fits the
 * space the modal gives it. Unpinned components are found on the list tab.
 */
const ComponentDrawingPicker: React.FC<ComponentDrawingPickerProps> = ({
    building,
    components,
    pickableKeys,
    multiSelect,
    selectedKey,
    selectedKeys,
    onPick,
}) => {
    const structure = building.model.structure;
    const views = useMemo(() => drawingViews(building.model), [building]);
    const [currentViewId, setCurrentViewId] = useState<string | null>(null);
    const [stageRef, stage] = useElementSize<HTMLDivElement>();

    const view = useMemo(() => {
        const chosen = currentViewId ? views.find(candidate => viewId(candidate) === currentViewId) : undefined;
        return chosen ?? defaultDrawingView(views);
    }, [views, currentViewId]);

    const levels = useMemo(
        () => Object.entries(structure?.levels || {}).map(([key, level]) => (level.key ? level : { ...level, key })),
        [structure]
    );
    const satelliteView = views.find(candidate => candidate.kind === 'satellite');
    const roofPlanView = views.find(candidate => candidate.kind === 'roofPlan');

    // Everything pinned on the picture is drawn, muted when it cannot be picked.
    const { markers, muted } = useMemo(() => {
        if (!view) return { markers: [], muted: new Set<string>() };
        const placed = components.filter(component => isPlacedOn(component, view));
        const drawn = view.kind === 'satellite' && view.imageKey ? asSatelliteMarkers(placed, view.imageKey) : placed;
        return {
            markers: drawn,
            muted: new Set(drawn.filter(component => !pickableKeys.has(component.key)).map(component => component.key)),
        };
    }, [view, components, pickableKeys]);

    if (!view) return null;

    // The drawing is width-driven (its height follows the image's aspect
    // ratio), so fitting the stage means capping the width at what the
    // stage's height allows. Unmeasured (first paint, tests): full width.
    const image = view.image;
    const aspect = image && image.width > 0 && image.height > 0 ? image.height / image.width : null;
    const fitWidth = aspect && stage.height > 0 && stage.width > 0
        ? Math.min(stage.width, stage.height / aspect)
        : undefined;

    return (
        <div className="component-drawing-picker">
            <LevelNavigator
                levels={levels}
                selectedKey={view.kind === 'level' ? view.levelKey : undefined}
                onSelect={levelKey => setCurrentViewId(viewId({ kind: 'level', levelKey }))}
                satellite={satelliteView ? { selected: view.kind === 'satellite', onSelect: () => setCurrentViewId('satellite') } : undefined}
                roofPlan={roofPlanView ? { selected: view.kind === 'roofPlan', onSelect: () => setCurrentViewId('roofPlan') } : undefined}
                className="mb-3"
            />

            <div ref={stageRef} className="component-drawing-picker__stage">
                {image && view.imageKey ? (
                    <div className="component-drawing-picker__fit" style={{ width: fitWidth }}>
                        <BlueprintWithMarkers
                            key={view.imageKey}
                            blueprint={image}
                            currentBlueprintKey={view.imageKey}
                            building={building}
                            utilities={markers}
                            selectedUtilityKey={multiSelect ? undefined : selectedKey}
                            selectedUtilityKeys={multiSelect ? selectedKeys : undefined}
                            mutedUtilityKeys={muted}
                            onUtilityClick={onPick}
                            hideTitle
                            compactMarkers
                        />
                    </div>
                ) : (
                    <div className="component-drawing-picker__placeholder">
                        Ingen ritning för {view.label}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ComponentDrawingPicker;
